import React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import { Button } from '../../../components/core';
import { DialogCloseButton } from '../../../components/core/DialogCloseButton';

type Props = {
  open: boolean;
  agentName: string;
  isSaving?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

export const DeleteAgentModal = ({
  open,
  agentName,
  isSaving = false,
  onClose,
  onConfirm,
}: Props): JSX.Element => (
  <Dialog
    open={open}
    onClose={isSaving ? undefined : onClose}
    PaperProps={{
      sx: {
        borderRadius: 4,
        minWidth: { xs: '90%', md: '733px' },
        p: 3,
        position: 'relative',
      },
    }}
  >
    <DialogCloseButton onClose={onClose} disabled={isSaving} />
    <DialogTitle sx={{ fontWeight: 600, p: 0, mb: 1, pr: 5 }}>Delete agent</DialogTitle>
    <Typography sx={{ mb: 3, color: 'text.secondary', fontSize: 14, maxWidth: 560 }}>
      This will permanently remove this agent from your storefront. Shoppers will no longer be able
      to start conversations with it.
    </Typography>

    <DialogContent sx={{ p: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'secondary.light',
          borderRadius: 2,
          px: 2,
          py: 1.5,
          mb: 3,
        }}
        data-testid="ella-delete-agent-banner"
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'text.secondary',
            flexShrink: 0,
          }}
        />
        <Typography variant="medium">
          {agentName} is currently{' '}
          <Typography component="span" fontWeight="bold">
            offline.
          </Typography>
        </Typography>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'border.main',
          borderRadius: 2,
          p: 3,
          mb: 4,
        }}
      >
        <Typography variant="large" fontWeight="semibold" sx={{ mb: 1 }}>
          Delete {agentName} from your website
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
          This agent will be removed from Agent Hub and will no longer appear on your storefront.
          This cannot be undone.
        </Typography>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ '& button': { height: 48 } }}
      >
        <Button
          theme="secondary"
          text="Cancel"
          onClick={onClose}
          disabled={isSaving}
          dataTestId="ella-delete-agent-cancel"
          sx={{ minWidth: 120 }}
        />
        <Button
          theme="primary"
          text={isSaving ? 'Deleting...' : 'Delete'}
          onClick={() => {
            void onConfirm();
          }}
          disabled={isSaving}
          loading={isSaving}
          dataTestId="ella-delete-agent-confirm"
          sx={{ minWidth: 120 }}
        />
      </Stack>
    </DialogContent>
  </Dialog>
);
